import { createContext, useContext, useEffect, useState } from "react";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    getAdditionalUserInfo,
} from "firebase/auth";
import apiService from "../services/apiService";
import { auth } from "./firebase";
import * as Sentry from '@sentry/react';

const UserContext = createContext();

export const AuthContextProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const createUser = (email, password) => {
        return createUserWithEmailAndPassword(auth, email, password);
    };

    const logout = () => {
        Sentry.setUser(null);
        return signOut(auth);
    };

    const setupSentryUser = (uid) => {
        Sentry.setUser({ id: uid });
    };

    const signIn = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const registerInternalUserWithSso = async (user) => {
        const name = user.displayName;
        try {
            let character = apiService.getDefaultCharacterSettings();
            character.firstName = name;
            character.lastName = name;
            character.characterName = name;
            return await apiService.updateUsersCharacter(character);
        } catch (error) {
            console.error("Error creating user:", error);
            throw error;
        }
    };

    const signInWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            const newUser = getAdditionalUserInfo(result).isNewUser;
            const user = result.user;
            setUser(user);

            if (newUser) {
                await registerInternalUserWithSso(user);
            }
            setupSentryUser(user.uid);
            return user;
        } catch (error) {
            console.error("Error during Google sign-in:", error);
            throw error;
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                setupSentryUser(currentUser.uid);
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    return (
        <UserContext.Provider value={{ createUser, user, logout, signIn, signInWithGoogle, isLoading }}>
            {children}
        </UserContext.Provider>
    );
};

export const UserAuth = () => {
    return useContext(UserContext);
};
